import {getHeaderScope} from './constants';
import {collectGroupTree} from './group-tree';
import type {HeaderScope, PhotoRequirement} from './types';

/** Upper bound of a scope as a number (undefined `to` = open-ended = Infinity). */
const upper = (to?: number) => to ?? Infinity;

export type HeaderScopeMode = 'first' | 'every' | 'custom';

/** Classify a header scope into the three preset UI modes. `first` = page 1 only,
 *  `every` = page 1 onward with no upper bound, anything else is a `custom` range. */
export function deriveHeaderScopeMode(scope: HeaderScope): HeaderScopeMode {
  if (scope.from === 1 && scope.to === 1) return 'first';
  if (scope.from === 1 && scope.to === undefined) return 'every';
  return 'custom';
}

/** Human-readable description of the pages a header shows on (e.g. "every page",
 *  "from page 3", "pages 2–4"). Mirrors {@link deriveHeaderScopeMode}'s presets. */
export function formatHeaderScope(scope: HeaderScope): string {
  if (scope.from === 1 && scope.to === 1) return 'first page';
  if (scope.from === 1 && scope.to === undefined) return 'every page';
  if (scope.to === undefined) return `from page ${scope.from}`;
  return `pages ${scope.from}–${scope.to}`;
}

export interface HeaderOverlap {
  id: string;
  label: string;
  scope: HeaderScope;
}

/** Two scopes overlap when they share at least one page. */
function scopesOverlap(a: HeaderScope, b: HeaderScope): boolean {
  return a.from <= upper(b.to) && b.from <= upper(a.to);
}

/**
 * Header sentinels — other than `targetId` — whose page range intersects `newScope`.
 * These are the headers that applying `newScope` to the target would overwrite, and
 * what the confirmation dialog lists. Legacy loose headers (no sentinel) are ignored.
 */
export function findOverlappingHeaders(
  requirements: PhotoRequirement[],
  targetId: string,
  newScope: HeaderScope,
): HeaderOverlap[] {
  const overlaps: HeaderOverlap[] = [];
  for (const r of requirements) {
    if (r.type !== 'group' || !r.is_header || r.id === targetId) continue;
    const scope = getHeaderScope(r);
    if (scopesOverlap(scope, newScope)) {
      overlaps.push({id: r.id, label: r.label || 'Header', scope});
    }
  }
  return overlaps;
}

/** Deep-clone a header group tree with fresh ids, giving its sentinel `scope`. */
function cloneGroupTree(
  requirements: PhotoRequirement[],
  rootId: string,
  newId: () => string,
  scope: HeaderScope,
  out: PhotoRequirement[],
): void {
  const ids = collectGroupTree(requirements, rootId);
  const idMap = new Map<string, string>();
  for (const id of ids) idMap.set(id, newId());
  for (const r of requirements) {
    if (!ids.has(r.id)) continue;
    out.push({
      ...r,
      id: idMap.get(r.id)!,
      group_id: r.group_id !== undefined ? (idMap.get(r.group_id) ?? r.group_id) : undefined,
      header_scope: r.id === rootId ? scope : r.header_scope,
      repeat_on_every_page: r.id === rootId ? undefined : r.repeat_on_every_page,
    });
  }
}

/**
 * Apply `newScope` to the target header and rewrite every overlapping header so that
 * no two header ranges intersect afterwards. For each overlapping header X, the pages
 * outside the new range are preserved:
 *   - X fully inside the new range → removed
 *   - X overlaps only the start    → trimmed to end the page before the new range
 *   - X overlaps only the end      → trimmed to start the page after the new range
 *   - X spans the new range        → kept as its before-piece, cloned for its after-piece
 *
 * `newId` mints ids for the cloned (split) header. Returns the rewritten requirements.
 */
export function resolveHeaderScopeChange(
  requirements: PhotoRequirement[],
  targetId: string,
  newScope: HeaderScope,
  newId: () => string,
): PhotoRequirement[] {
  const sf = newScope.from;
  const st = upper(newScope.to);

  const removeIds = new Set<string>();
  const clones: PhotoRequirement[] = [];

  const rewritten = requirements.map((r) => {
    if (r.id === targetId) {
      return {...r, header_scope: newScope, repeat_on_every_page: undefined};
    }
    if (r.type !== 'group' || !r.is_header) return r;

    const scope = getHeaderScope(r);
    if (!scopesOverlap(scope, newScope)) return r;

    const xf = scope.from;
    const xt = upper(scope.to);
    const hasLeft = xf < sf; // pages before the new range survive
    const hasRight = st < xt; // pages after the new range survive (only if `st` is finite)

    if (!hasLeft && !hasRight) {
      // Fully covered by the new range → drop its whole group tree.
      for (const id of collectGroupTree(requirements, r.id)) removeIds.add(id);
      return r; // removed by the filter below
    }
    if (hasLeft && hasRight) {
      // Spans the new range → keep the before-piece here, clone the after-piece.
      cloneGroupTree(requirements, r.id, newId, {from: st + 1, to: scope.to}, clones);
      return {...r, header_scope: {from: xf, to: sf - 1}, repeat_on_every_page: undefined};
    }
    if (hasLeft) {
      return {...r, header_scope: {from: xf, to: sf - 1}, repeat_on_every_page: undefined};
    }
    // Overlaps only the end → start after the new range, keep the original upper bound.
    return {...r, header_scope: {from: st + 1, to: scope.to}, repeat_on_every_page: undefined};
  });

  const kept = rewritten.filter((r) => !removeIds.has(r.id));
  return [...kept, ...clones].map((r, i) => ({...r, order: i}));
}
